import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string | null;
}

/**
 * Root Error Boundary
 *
 * Catches fatal errors during app initialization and rendering.
 * Displays a recovery UI instead of a blank screen.
 *
 * NEVER displays PHI - only technical context for debugging.
 */
export class RootErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details (no PHI)
    console.error('[RootErrorBoundary] Fatal error:', error);
    console.error('[RootErrorBoundary] Component stack:', errorInfo.componentStack);

    this.setState({
      errorInfo: errorInfo.componentStack || null,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Ignore storage errors
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;

      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#0a0f1a',
          color: '#e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1rem',
            }}>
              ⚠️
            </div>
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              marginBottom: '1rem',
              color: '#f87171',
            }}>
              Application Error
            </h1>
            <p style={{
              color: '#9ca3af',
              marginBottom: '2rem',
              lineHeight: 1.6,
            }}>
              RadiologyLive.ai encountered an unexpected error during startup.
              This may be due to a configuration issue or network problem.
            </p>

            {isDev && this.state.error && (
              <div style={{
                backgroundColor: '#1f2937',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '2rem',
                textAlign: 'left',
                overflow: 'auto',
              }}>
                <p style={{
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  color: '#f87171',
                  marginBottom: '0.5rem',
                }}>
                  {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <pre style={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    whiteSpace: 'pre-wrap',
                    margin: 0,
                  }}>
                    {this.state.errorInfo}
                  </pre>
                )}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}>
              <button
                onClick={this.handleReload}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Reload Page
              </button>
              <button
                onClick={this.handleClearAndReload}
                style={{
                  backgroundColor: '#374151',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Clear Data & Reload
              </button>
            </div>

            <p style={{
              color: '#6b7280',
              fontSize: '0.875rem',
              marginTop: '2rem',
            }}>
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
